import { z } from "zod";
import { ENCOUNTER_DISCUSSION_PROMPT_VERSION, EncounterDiscussionHistoryEntrySchema, } from "./encounter-discussion.js";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * A free-form question about one patient's chart, asked outside a visit from the
 * chart assistant on the patient profile ("what was her hemoglobin over the last
 * three years?"). Answers only: a response never carries a chart write, an order
 * or a draft. The answer comes from the clinical discussion prompt, so that
 * prompt's version travels with every answer.
 */
export const PATIENT_CHART_QUESTION_MAX_MESSAGE_LENGTH = 8_000;
export const PatientChartQuestionRequestSchema = z
    .object({
    siteId: IdSchema,
    message: z.string().trim().min(1).max(PATIENT_CHART_QUESTION_MAX_MESSAGE_LENGTH),
    /** Earlier turns of this conversation: context for follow-ups, never chart evidence. */
    history: z.array(EncounterDiscussionHistoryEntrySchema).max(8).default([]),
})
    .strict();
/** One laboratory series (analyte and unit) the answer quoted, for charting from the lab read model. */
export const PatientChartQuestionLabSeriesSchema = z
    .object({
    analyteKey: z.string().min(1).max(320),
    /** The series unit as recorded; empty when the values carry none. */
    unit: z.string().max(80),
    display: z.string().min(1).max(240),
})
    .strict();
export const PatientChartQuestionResponseSchema = z
    .object({
    answer: z.string().trim().min(1).max(12_000),
    limitations: z.array(z.string().trim().min(1).max(1_000)).max(8),
    /** Each quote is the complete chart record it came from, never a fragment of one. */
    evidence: z
        .array(z
        .object({
        sourceId: z.string().trim().min(1).max(100),
        /** Plain-language name of the chart section, for display. */
        label: z.string().trim().min(1).max(120),
        quote: z.string().trim().min(1).max(2_000),
    })
        .strict())
        .max(12),
    labSeries: z.array(PatientChartQuestionLabSeriesSchema).max(3),
    model: z.string().trim().min(1).max(160),
    promptVersion: z.literal(ENCOUNTER_DISCUSSION_PROMPT_VERSION),
    context: z
        .object({
        assembledAt: IsoDateTimeSchema,
        /** Lab values the answer could read, of all the chart holds (newest first past the cap). */
        labResults: z
            .object({
            included: z.number().int().nonnegative(),
            total: z.number().int().nonnegative(),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
/**
 * Whether this deployment answers chart questions. The composer offers questions only
 * when it does; elsewhere every message goes to the record writer, as before.
 */
export const ChartAssistantCapabilitiesSchema = z.object({ questions: z.boolean() }).strict();
//# sourceMappingURL=patient-chart-question.js.map